import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styled from '@emotion/styled';
import { Plus, Download, FileDown, Eye, ArrowLeft, User, EyeOff, Columns, Filter } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import PanelButton from '../PanelButton';
import FilterButton, { FilterButtonGroup } from '../FilterButton';
import StableTypeahead from '../StableTypeahead';
import { addNotification } from '../../store/slices/uiSlice';
import { setShowSystemValues, addAdvancedFilter, removeAdvancedFilter, clearAdvancedFilters } from '../../store/slices/databaseSlice';
import { apiFetch } from '../../utils/apiUtils';
import { useAuth } from '../../contexts/AuthContext';
import { databaseAPI } from '../../services/api';
import AdvancedFiltersModal from '../AdvancedFiltersModal';

const SectionTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  color: #e0e0e0;
  font-size: 0.875rem;
  font-weight: 600;
`;

const SectionContainer = styled.div`
  margin-bottom: 1.5rem;
`;

const Explainer = styled.div`
  color: #a0a0a0;
  font-size: 0.7rem;
  margin-bottom: 0.75rem;
  line-height: 1.4;
`;

const SelectedItemsContainer = styled.div`
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 0.75rem;
  margin-top: 0.75rem;
  max-height: 200px;
  overflow-y: auto;
`;

const SelectedItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.5rem;
  background: #3a3a3a;
  border-radius: 4px;
  margin-bottom: 0.5rem;
  
  &:last-child {
    margin-bottom: 0;
  }
`;

const SelectedItemText = styled.span`
  color: #e0e0e0;
  font-size: 0.75rem;
`;

const RemoveButton = styled.button`
  background: transparent;
  border: none;
  color: #ef4444;
  cursor: pointer;
  font-size: 0.75rem;
  padding: 0.25rem 0.5rem;
  
  &:hover {
    color: #dc2626;
  }
`;

const LoanHeader = styled.div`
  background: #3a3a3a;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
`;

const LoanTitle = styled.div`
  color: #e0e0e0;
  font-size: 0.875rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const LoanMeta = styled.div`
  color: #a0a0a0;
  font-size: 0.7rem;
  line-height: 1.6;
`;

const CaseListContent = ({ 
  latestRecords = [], 
  combinedData = [], 
  loanIdsData = [],
  onStatusFilterChange,
  onSelectedLoansChange,
  onSelectedBorrowersChange,
  onFilteredLoansChange,
  statusFilter = 'all',
  selectedLoans = [],
  selectedBorrowers = [],
  onShowColumnModal
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  
  // Get the actual OCR records from Redux store (has all the fields)
  const allRecords = useSelector(state => state.database.records || []);
  
  // Get showSystemValues from Redux store
  const showSystemValues = useSelector(state => state.database.showSystemValues || false);
  
  // Toggle system values function
  const handleToggleSystemValues = () => {
    dispatch(setShowSystemValues(!showSystemValues));
  };
  const { loanId } = useParams(); // Get loanId from URL if viewing a specific loan
  const { user: authUser } = useAuth();
  const currentUser = authUser; // Use authenticated user from AuthContext

  // Advanced filters state
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [editingFilter, setEditingFilter] = useState(null); // Filter being edited (or null for new)
  const advancedFilters = useSelector(state => state.database.advancedFilters || []); // Get from Redux
  const [activeFields, setActiveFields] = useState([]);

  // Load active fields for advanced filters
  React.useEffect(() => {
    const loadActiveFields = async () => {
      try {
        const response = await apiFetch('/system-files/fields/stats');
        if (response.success) {
          const activeFieldObjects = response.fields
            .filter(field => field.status === 'active');
          setActiveFields(activeFieldObjects);
        }
      } catch (error) {
        console.error('Error loading active fields:', error);
      }
    };
    loadActiveFields();
  }, []);

  const handleApplyAdvancedFilter = (filter) => {
    if (!filter) {
      // Clear all filters
      dispatch(clearAdvancedFilters());
    } else {
      // Add or update filter
      dispatch(addAdvancedFilter(filter));
    }
  };

  const handleRemoveFilter = (fieldName) => {
    dispatch(removeAdvancedFilter(fieldName));
  };

  const handleOpenAdvancedFilters = (filter = null) => {
    setEditingFilter(filter);
    setShowAdvancedFilters(true);
  };

  const handleGenerateReport = () => {
    // TODO: Call API endpoint to generate a custom report with filters
    // Filters: status, selected loan IDs, selected borrowers
  };

  const handleExportCSV = async () => {
    try {
      dispatch(addNotification({
        type: 'info',
        message: 'Generating export...',
        duration: 2000
      }));
      
      // Prepare filters for the API
      const selectedLoanIds = selectedLoans.length > 0 
        ? selectedLoans.map(l => typeof l === 'string' ? l : l.loan_id)
        : null;
      
      // Extract loan IDs from borrowers (borrowers have loan_ids arrays)
      const selectedBorrowerLoanIds = selectedBorrowers.length > 0
        ? selectedBorrowers.flatMap(b => {
            if (typeof b === 'string') {
              // If it's just a string, we need to find the borrower object
              const borrowerObj = combinedData.find(borrower => borrower.borrower_name === b);
              return borrowerObj?.loan_ids || [];
            } else if (b.loan_ids && Array.isArray(b.loan_ids)) {
              // Borrower object with loan_ids array
              return b.loan_ids;
            } else if (b.loan_id) {
              // Single loan_id
              return [b.loan_id];
            }
            return [];
          })
        : null;
      
      // Get visible columns from DatabaseViewer
      const visibleColumns = window.caseListVisibleColumns || {};
      const activeFieldsFromViewer = window.caseListActiveFields || [];
      
      // Build list of visible field names
      const visibleFieldNames = activeFieldsFromViewer
        .filter(field => {
          const fieldName = typeof field === 'string' ? field : field.name;
          const fieldUuid = typeof field === 'string' ? fieldName : (field.field_uuid || field.uuid || fieldName);
          // Include if not explicitly set to false
          return visibleColumns[fieldUuid] !== false && visibleColumns[fieldName] !== false;
        })
        .map(field => typeof field === 'string' ? field : field.name);
      
      const filters = {
        selected_loan_ids: selectedLoanIds && selectedLoanIds.length > 0 ? selectedLoanIds : null,
        selected_borrower_loan_ids: selectedBorrowerLoanIds && selectedBorrowerLoanIds.length > 0 ? selectedBorrowerLoanIds : null,
        status_filter: statusFilter || 'all',
        advanced_filters: advancedFilters && advancedFilters.length > 0 ? advancedFilters : null,
        visible_columns: visibleFieldNames && visibleFieldNames.length > 0 ? visibleFieldNames : null
      };
      
      // Call the export API endpoint
      const response = await databaseAPI.exportCaseList(filters);
      
      // response.data is already a Blob when responseType is 'blob'
      const blob = response.data;
      
      // Get filename from Content-Disposition header or use default
      let filename = `case-list-export-${new Date().toISOString().split('T')[0]}.xlsx`;
      const contentDisposition = response.headers['content-disposition'];
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }
      
      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.setAttribute('href', url);
      link.setAttribute('download', filename);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      
      dispatch(addNotification({
        type: 'success',
        message: '✅ Report exported successfully!',
        duration: 3000
      }));
      
    } catch (error) {
      console.error('Error exporting case list:', error);
      const errorMessage = error.response?.data?.detail || error.message || 'Failed to export data';
      dispatch(addNotification({
        type: 'error',
        message: `❌ ${errorMessage}`,
        duration: 3000
      }));
    }
  };


  return (
    <div>
      {/* Show loan info if viewing a specific loan */}
      {loanId && (
        <SectionContainer>
          <LoanHeader>
            <LoanTitle>Viewing Loan: {loanId}</LoanTitle>
            <LoanMeta>
              Review documents and field extractions for this case
            </LoanMeta>
          </LoanHeader>
          <PanelButton onClick={() => navigate('/database')}>
            <ArrowLeft size={16} />
            Back to Case List
          </PanelButton>
        </SectionContainer>
      )}


      {/* Multi-Select Loan IDs */}
      <SectionContainer>
        <SectionTitle>Filter by Loan ID</SectionTitle>
        <Explainer>
          Select specific loan IDs to include in your report or analysis
        </Explainer>
        {/* Debug: Log the data being passed */}
        
        <StableTypeahead
          items={(loanIdsData || []).map(loan => ({ 
            id: loan.loan_id, 
            name: loan.loan_id,
            loan_id: loan.loan_id 
          }))}
          selectedItems={selectedLoans.map(loanId => ({
            id: loanId,
            name: loanId,
            loan_id: loanId
          }))}
          onSelectionChange={(newSelection) => {
            // Filter out duplicates by loan_id
            const uniqueSelection = newSelection.filter((item, index, self) => 
              index === self.findIndex(t => t.loan_id === item.loan_id)
            );
            
            // Extract loan IDs
            const loanIds = uniqueSelection.map(item => item.loan_id);
            
            // Find which borrowers have these loan IDs selected
            const selectedBorrowers = (combinedData || []).filter(borrower => 
              borrower.loan_ids && borrower.loan_ids.some(loanId => loanIds.includes(loanId))
            );
            
            // Update both selections
            onSelectedLoansChange && onSelectedLoansChange(loanIds);
            onSelectedBorrowersChange && onSelectedBorrowersChange(selectedBorrowers);
            
            // Trigger database call for filtered loans
            onFilteredLoansChange && onFilteredLoansChange(loanIds);
          }}
          placeholder="Search by Loan ID..."
          getItemLabel={(item) => item.name || item.loan_id || String(item)}
          getItemValue={(item) => item.id || item.loan_id || String(item)}
          isDark={true}
        />
      </SectionContainer>

      {/* Multi-Select Borrowers */}
      <SectionContainer>
        <SectionTitle>Filter by Borrower</SectionTitle>
        <Explainer>
          Select specific borrowers to include in your report or analysis
        </Explainer>
            <StableTypeahead
              items={(() => {
                const borrowerItems = (combinedData || [])
                  .map(borrower => ({ 
                    id: borrower.borrower_name, 
                    name: `${borrower.borrower_name} (${borrower.loan_count} loans)`,
                    borrower_name: borrower.borrower_name,
                    loan_ids: borrower.loan_ids
                  }));
                // console.log('🔍 Borrower filter items:', borrowerItems);
                // console.log('🔍 Combined data sample:', combinedData.slice(0, 3));
                return borrowerItems;
              })()}
              selectedItems={selectedBorrowers.map(borrowerName => ({
                id: borrowerName,
                name: borrowerName,
                borrower_name: borrowerName
              }))}
              onSelectionChange={(newSelection) => {
                // Filter out duplicates by borrower_name
                const uniqueSelection = newSelection.filter((item, index, self) => 
                  index === self.findIndex(t => t.borrower_name === item.borrower_name)
                );
                
                // Extract all loan IDs from selected borrowers
                const allLoanIds = uniqueSelection.flatMap(borrower => borrower.loan_ids || []);
                
                // Update both borrower selection and loan ID selection
                onSelectedBorrowersChange && onSelectedBorrowersChange(uniqueSelection);
                onSelectedLoansChange && onSelectedLoansChange(allLoanIds);
                
                // Trigger database call for filtered loans
                onFilteredLoansChange && onFilteredLoansChange(allLoanIds);
              }}
              placeholder="Search by Borrower Name..."
              getItemLabel={(item) => item.name || item.borrower_name || String(item)}
              getItemValue={(item) => item.id || item.loan_id || String(item)}
              isDark={true}
            />
      </SectionContainer>

      {/* Advanced Filters */}
      <SectionContainer>
        <SectionTitle>Advanced Filters</SectionTitle>
        <Explainer>
          Filter by specific field values across all cases
        </Explainer>
        <PanelButton 
          onClick={() => handleOpenAdvancedFilters(null)}
        >
          <Filter size={16} />
          {advancedFilters.length > 0 ? `Add Another Filter (${advancedFilters.length} active)` : 'Add Filter'}
        </PanelButton>
        
        {/* Show active filters */}
        {advancedFilters.length > 0 && (
          <div style={{ marginTop: '0.75rem' }}>
            {advancedFilters.map((filter, index) => (
              <div 
                key={filter.fieldName}
                data-filter-chip
                onClick={(e) => {
                  // Don't trigger edit if clicking the remove button
                  if (!e.target.closest('[data-filter-chip-remove]')) {
                    handleOpenAdvancedFilters(filter);
                  }
                }}
                style={{ 
                  marginBottom: '0.5rem',
                  padding: '0.5rem',
                  background: 'rgba(255, 255, 255, 0.05)',
                  border: 'none',
                  borderRadius: '4px',
                  fontSize: '0.75rem',
                  cursor: 'pointer'
                }}
              >
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  marginBottom: '0.25rem'
                }}>
                  <span 
                    data-filter-chip-title
                    style={{ 
                      color: '#e0e0e0', 
                      fontWeight: '600'
                    }}
                  >
                    {filter.fieldName}
                  </span>
                  <button
                    data-filter-chip-remove
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFilter(filter.fieldName);
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      color: '#9ca3af',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      fontSize: '1rem',
                      lineHeight: 1
                    }}
                    title="Remove filter"
                  >
                    ×
                  </button>
                </div>
                <div style={{ color: '#6b7280' }}>
                  {filter.values && filter.values.length > 0 && (
                    <div>
                      {filter.values.length} value(s): {filter.values.slice(0, 2).join(', ')}
                      {filter.values.length > 2 && `, +${filter.values.length - 2} more`}
                    </div>
                  )}
                  {filter.matchStatuses && filter.matchStatuses.length > 0 && (
                    <div style={{ marginTop: filter.values?.length > 0 ? '0.25rem' : '0' }}>
                      Status: {filter.matchStatuses.map(s => {
                        const statusLabels = {
                          perfect: 'Match',
                          maybe: 'Partial',
                          mismatch: 'No Match',
                          no_data: 'No Data'
                        };
                        return statusLabels[s] || s;
                      }).join(', ')}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionContainer>

      {/* Table Controls */}
      <SectionContainer>
        <SectionTitle>Table Controls</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <PanelButton 
            onClick={handleToggleSystemValues}
          >
            {showSystemValues ? <EyeOff size={16} /> : <Eye size={16} />}
            {showSystemValues ? 'Hide' : 'Show'} System Values
          </PanelButton>
          
          <PanelButton 
            onClick={onShowColumnModal}
          >
            <Columns size={16} />
            Columns
          </PanelButton>
        </div>
      </SectionContainer>

      {/* Export and Case Management */}
      <SectionContainer>
        <SectionTitle>Case Management</SectionTitle>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          <PanelButton onClick={handleExportCSV}>
            <FileDown size={16} />
            Export Report
          </PanelButton>
          {/* NOTE: CreateNewLoan deprecated - TODO: Add new loan creation via modal/dialog */}
          {/* <PanelButton onClick={() => navigate('/create')}>
            <Plus size={16} />
            New Case
          </PanelButton> */}
        </div>
      </SectionContainer>

      {/* Advanced Filters Modal */}
      <AdvancedFiltersModal
        isOpen={showAdvancedFilters}
        onClose={() => setShowAdvancedFilters(false)}
        onApply={handleApplyAdvancedFilter}
        activeFields={activeFields}
        currentFilter={editingFilter}
      />
    </div>
  );
};

export default CaseListContent;
